/**
 * Helpers for school exam config when "Use Exam Schedule" is off:
 * API uses separate start_date (YYYY-MM-DD), start_time, end_time (HH:mm:ss).
 */

function timeToMinutes(t) {
  if (!t || typeof t !== "string") {
    return NaN;
  }
  const pad = (x) => (x.length === 5 ? `${x}:00` : x);
  const p = pad(t.trim());
  const parts = p.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  const s = Number(parts[2] || 0);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return NaN;
  }

  return h * 60 + m + s / 60;
}

/** Format time for API (HH:mm:ss). */
export function formatTimeForApi(timeStr) {
  if (!timeStr || typeof timeStr !== "string") {
    return null;
  }
  const t = timeStr.trim();
  if (t.length === 5) {
    return `${t}:00`;
  }
  if (t.length >= 8) {
    return t.substring(0, 8);
  }

  return t;
}

/**
 * Minutes between start and end time on the same day; null if invalid or end ≤ start.
 */
export function getScheduleWindowMinutes(startTime, endTime) {
  const a = timeToMinutes(startTime);
  const b = timeToMinutes(endTime);
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return null;
  }
  const diff = b - a;

  return diff > 0 ? Math.round(diff) : null;
}

/** End time must be strictly after start time (same calendar day). */
export function isExamEndAfterStart(_examDate, startTime, endTime) {
  if (!startTime || !endTime) {
    return true;
  }
  const a = timeToMinutes(startTime);
  const b = timeToMinutes(endTime);
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return true;
  }

  return b > a;
}

function normalizeDateOnly(str) {
  if (!str || typeof str !== "string") {
    return "";
  }
  if (str.includes("T")) {
    return str.substring(0, 10);
  }
  if (str.length >= 10) {
    return str.substring(0, 10);
  }

  return str;
}

/** HTML time input expects HH:mm. */
function normalizeTimeForInput(str) {
  if (!str || typeof str !== "string") {
    return "";
  }
  if (str.length >= 5) {
    return str.substring(0, 5);
  }

  return str;
}

function splitLegacyCombined(startDateStr, endDateStr) {
  const parseOne = (str) => {
    if (!str || typeof str !== "string") {
      return { date: "", time: "" };
    }
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) {
      const dateOnly = str.length >= 10 ? str.substring(0, 10) : "";

      return { date: dateOnly, time: "" };
    }
    const pad = (n) => String(n).padStart(2, "0");

    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
    };
  };

  const s = parseOne(startDateStr);
  const e = parseOne(endDateStr);

  return {
    exam_date: s.date || e.date,
    start_time: normalizeTimeForInput(s.time),
    end_time: normalizeTimeForInput(e.time)
  };
}

/**
 * Map API/config into form fields. Supports separate start_date + start_time + end_time,
 * or legacy combined start_date / end_date datetimes.
 */
export function loadScheduleOffFieldsFromApi(config = {}, rawData = {}) {
  const startDateRaw = config.start_date ?? rawData?.start_date ?? "";
  const startTimeRaw = config.start_time ?? rawData?.start_time ?? "";
  const endTimeRaw = config.end_time ?? rawData?.end_time ?? "";
  const endDateRaw = config.end_date ?? rawData?.end_date ?? "";

  if (startTimeRaw || endTimeRaw) {
    return {
      exam_date: normalizeDateOnly(startDateRaw),
      start_time: normalizeTimeForInput(startTimeRaw),
      end_time: normalizeTimeForInput(endTimeRaw)
    };
  }

  return splitLegacyCombined(startDateRaw, endDateRaw);
}
